/*******************************************************************************************
   _____            __              ____   ____                         .__               
  /  _  \   _______/  |________  ___\   \ /   /____   ____  __ __  _____|__|____    ____  
 /  /_\  \ /  ___/\   __\_  __ \/  _ \   Y   // __ \ /    \|  |  \/  ___/  \__  \  /    \ 
/    |    \\___ \  |  |  |  | \(  <_> )     /\  ___/|   |  \  |  /\___ \|  |/ __ \|   |  \
\____|__  /____  > |__|  |__|   \____/ \___/  \___  >___|  /____//____  >__(____  /___|  /
        \/     \/                                 \/     \/           \/        \/     \/ 
********************************************************************************************
Footer
********************************************************************************************

Author:     Nicholas Hamilton, PhD
Email:      nicholasehamilton@gmail.com
Date:       22nd December 2020

*******************************************************************************************/
import React                            from 'react';
import moment                           from 'moment';
import {titleCase}                      from 'title-case';
import { 
    styled,
    useTheme,
    alpha,
    Box, 
    Toolbar, 
    Paper as PaperMUI, 
    ButtonGroup,
    Typography
}                                       from '@mui/material';
import { 
    LogoMonogram, 
    Copyright,
    GlobalLoader,
    Locale,
    ThemeSwitch,
    SocialAccounts,
    QRCode,
    StripePaymentImages,
    AdminOnlyTag,
    useCountDown,
    LoadingData
}                                       from 'components';
import { 
    AboutLocation,
    FAQLocation,
    PolicyPrivacyLocation,
    PolicyTermsLocation,
    PolicyCookiesLocation,
    UserContactLocation
}                                       from 'router/locations';
import { 
    useTranslation, 
    useNetwork,
    useDevice,
    useAuth,
    useLocale,
    useUser
}                                       from 'contexts';
import { 
    useBreakpointQueries,
    useImageCDN, 
    useSoftwareVersion,
    useClock
}                                       from 'hooks';
import FooterButton                     from './FooterButton';
import universe                         from 'resources/universe/universe.jpg'
import config                           from '../../config';

const normalizePadding = (value) => {
    if (typeof value === 'number') return `${value}px`;
    if (typeof value === 'string') return value;
    return '0px';
};

const AdminAuthReport = () => {
    const { mdUp : minSize }            = useBreakpointQueries();
    const {now}                         = useClock();
    const {formatDateTime}              = useLocale();
    const {
        expiresAt,
        issuedAt,
        refreshingAt,
        isRefreshing
    }                                   = useAuth();
    
    const {
        isAuthenticated,
        isAdmin
    }                                   = useUser();
    const {
        result : expiresAtCountDownResult
    }                                   = useCountDown({to : expiresAt || now, now});
    const {
        result : refreshingAtCountDownResult
    }                                   = useCountDown({to : refreshingAt || now, now});

    const [
        issuedAtFormatted,
        expiresAtFormatted,
        refreshingAtFormatted
    ]                                   = React.useMemo(() => (
        [issuedAt,expiresAt,refreshingAt]
            .map(mom => moment.isMoment(mom) 
                ? formatDateTime(mom) 
                : ''
            )
    ), [expiresAt, formatDateTime, issuedAt, refreshingAt])

    if(!isAuthenticated || !isAdmin || !(issuedAt || expiresAt || refreshingAt))
        return null;

    return (
        <Box sx={{mx:'auto',px:2,width:'fit-content'}}>
            <Box sx={{border:'1px solid red',mt:3, p:0.5,pr:1, borderRadius:2, fontSize:10, width:'fit-content'}}>
                {
                    !minSize &&
                    <Box width="100%" align="center" sx={{my:1}}>
                        <AdminOnlyTag />
                    </Box>
                }
                <Box display="flex" alignItems="center">
                    <Box flexGrow={1} />
                    {
                        minSize &&
                        <Box sx={{mr:1}}>
                            <AdminOnlyTag />
                        </Box>
                    }
                    <Box>
                        Access Token {
                            [
                                issuedAt        && `Issued: ${issuedAtFormatted}`,
                                expiresAt       && `Expires: ${expiresAtFormatted} in ${expiresAtCountDownResult}`,
                                refreshingAt    && `Refreshing: ${refreshingAtFormatted} in ${refreshingAtCountDownResult}`
                            ]
                                .filter(Boolean)
                                .join(', ')
                        }.
                    </Box>
                    {
                        isRefreshing &&
                        <Box id="loadingData" ml={1}>
                            <LoadingData color="red" size={12} />
                        </Box>
                    }
                    <Box flexGrow={1} />
                </Box>
            </Box>
        </Box>
    )
}

const Paper = styled(PaperMUI, {
    shouldForwardProp: (prop) => !['showRule', 'color', 'height', 'extraPaddingBottom'].includes(prop),
})(({ theme, showRule, color, height, extraPaddingBottom }) => {
    const basePaddingVert   = theme.spacing(6);
    const extraPadding      = normalizePadding(extraPaddingBottom);
    return {
        minHeight           : height,
        borderTop           : showRule ? `${theme.spacing(3)} solid ${color}` : 'none',
        paddingTop          : basePaddingVert,
        paddingBottom       : `calc(${basePaddingVert} + ${extraPadding})`,
        paddingLeft         : theme.spacing(2),
        paddingRight        : theme.spacing(2),
        color               : theme.palette.primary.contrastText,
        backgroundColor     : theme.palette.primary.dark,
    };
});

export const Footer = ({ 
    height      = 225, 
    showRule    = true,
     ...props
}) => {
    const [t]                           = useTranslation();
    const {safeAreaInsets}              = useDevice();
    const theme                         = useTheme();
    const convert                       = useImageCDN() 
    const { isAPIHealthy }              = useNetwork();
    const { isEnabled, versionClient }  = useSoftwareVersion();
    const { mdUp : minSize }            = useBreakpointQueries();
    const color                         = React.useMemo(() => theme.palette.primary.main, [theme.palette.primary.main])
    
    // Construct the Server Status Term
    const serverStatus                  = React.useMemo(() => {

        const status = [];
    
        // Add API health status
        status.push(
            isAPIHealthy 
                ? 'HEALTHY' 
                : 'UNREACHABLE'
        );

        // Add version info if enabled and available
        if (isEnabled && versionClient)
            status.push(`v${versionClient}`);

        // Join status elements into a single string
        return status.length 
            ? status.join(', ') 
            : 'No status available';
        
    },[isAPIHealthy, isEnabled, versionClient])

    return (
        <Paper 
            extraPaddingBottom  = {safeAreaInsets?.bottom}
            square              = {true}
            {...props}
            sx                  = {{
                ...props.sx,
                backgroundImage : `linear-gradient(${alpha(color,0.75)},${alpha(color,0.25)}), url(${convert(universe,{operation:'width',width:1000})})`,
                backgroundSize  : 'cover',
                textAlign       : 'center'
            }}
        >
            <Box display="flex" justifyContent="center" mx = 'auto' mb = {4} >
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <LogoMonogram dark={false} />
                    <QRCode containerProps={{ml:1}}/>
                </Box>
            </Box>

            <Box>
                <SocialAccounts />
            </Box>

            {
                minSize &&
                <Toolbar sx={{justifyContent:'center'}}>
                    <ButtonGroup variant="text">
                        <FooterButton to={AboutLocation.path}>           
                            {t('common.about')}
                        </FooterButton>
                        <FooterButton to={FAQLocation.path}>             
                            {t('common.faq')}
                        </FooterButton>
                        <FooterButton to={PolicyTermsLocation.path}>     
                            {t('pages.policies.termsAndConditions.title')}
                        </FooterButton>
                        <FooterButton to={PolicyPrivacyLocation.path}>   
                            {t('pages.policies.privacyPolicy.title')}
                        </FooterButton>
                        <FooterButton to={PolicyCookiesLocation.path}>   
                            {t('pages.policies.cookiePolicy.title')}
                        </FooterButton>
                        <FooterButton to={UserContactLocation.path}>     
                            {t('common.contact')}
                        </FooterButton>
                        <ThemeSwitch/>
                    </ButtonGroup>
                </Toolbar>
            }
            {
                !minSize &&
                <ThemeSwitch condensed={true}/>
            }

            <Box sx={{maxWidth:300,mx:'auto',mb:2}}>
                <StripePaymentImages mode="dark"/>
            </Box>

            <Typography component={Box} variant="body2" sx={{color:'inherit','& > * + *':{mt:1}}}>
                <Box>
                    {titleCase(config.env)} Server is: {serverStatus}
                </Box>
                <Box>
                    <Locale />
                </Box>
                <Box>
                    <Copyright/>
                </Box>
                <Box>
                    <AdminAuthReport />
                </Box>
                <Box sx={{minHeight:40}}>
                    <GlobalLoader color={theme.palette.primary.contrastText}/>
                </Box>
            </Typography>    
        </Paper>
    );
}

export default Footer;